import { useState } from "react";
import { supabase } from "./supabaseClient";
import { sanitizeText } from './utils/sanitize';
import logo from "./logo.png";
import { ArrowUpRight, Eye, EyeOff, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

const STAGES = ["Pre-Seed", "Seed", "Series A", "Series B", "Series C+"];
const SECTORS = ["SaaS", "Fintech", "Healthtech", "Edtech", "CleanTech", "E-commerce", "AI/ML", "Biotech", "Consumer", "DeepTech", "Other"];
const AUM_OPTIONS = ["Under $1M", "$1M - $10M", "$10M - $50M", "$50M - $200M", "$200M+"];
const ROLES = ["Analyst", "Associate", "VP", "Principal", "Partner", "GP (General Partner)", "Founder / Operator"];
const HEAR_OPTIONS = ["LinkedIn", "Twitter / X", "A VC friend", "Google Search", "Newsletter", "Conference / Event", "Other"];

const inputStyle = {
  width: "100%", padding: "12px 16px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 10, outline: "none",
  fontSize: 14, fontWeight: 500, color: "#fff",
  fontFamily: "inherit", boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const labelStyle = {
  fontSize: 11, fontWeight: 700,
  color: "rgba(255,255,255,0.4)",
  textTransform: "uppercase", letterSpacing: "0.1em",
  display: "block", marginBottom: 8,
};

function friendlyError(msg = '') {
  const m = (msg || '').toLowerCase();
  if (m.includes('invalid') || m.includes('credentials') || m.includes('not found'))
    return "Please check the email address and try again.";
  if (m.includes('already registered') || m.includes('already exists') || m.includes('duplicate'))
    return "An account with this email already exists.";
  if (m.includes('row-level security') || m.includes('rls'))
    return "Something went wrong. Please try again.";
  return "Something went wrong. Please try again.";
}

function PillSelect({ options, selected, onToggle, multi = true }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map(opt => {
        const isSelected = multi ? selected.includes(opt) : selected === opt;
        return (
          <button key={opt} onClick={() => onToggle(opt)}
            style={{ padding: "6px 14px", borderRadius: 100, fontSize: 12, fontWeight: 600, border: "1px solid", cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s", background: isSelected ? "rgba(220,38,38,0.15)" : "transparent", borderColor: isSelected ? "#dc2626" : "rgba(255,255,255,0.12)", color: isSelected ? "#dc2626" : "rgba(255,255,255,0.5)" }}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

// ── LOGIN ──
function LoginForm({ onLogin, onSwitchToSignup, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) { setError(friendlyError(error.message)); setLoading(false); return; }

    // Check approval status
    const { data: profile } = await supabase.from("profiles").select("status").eq("id", data.user.id).single();
    if (profile?.status === "pending") {
      setError("Your application is under review. We'll notify you within 24 hours.");
      await supabase.auth.signOut();
      setLoading(false); return;
    }
    if (profile?.status === "rejected") {
      setError("Your application was not approved. Email us to discuss.");
      await supabase.auth.signOut();
      setLoading(false); return;
    }
    if (profile?.status === "approved") {
      onLogin(); return;
    }
    setError("Your account is not yet set up. Please contact support.");
    await supabase.auth.signOut();
    setLoading(false);
  };

  const handleGoogle = async () => {
    setLoading(true); setError("");
    const { error } = await supabase.auth.signInWithOAuth({ provider: "google", options: { redirectTo: `${window.location.origin}/dashboard` } });
    if (error) { setError(friendlyError(error.message)); setLoading(false); }
  };

  return (
    <div autoComplete="off">
      <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 28, fontWeight: 400, color: "#fff", margin: "0 0 6px", letterSpacing: "-0.5px" }}>Welcome back.</h1>
      <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", margin: "0 0 28px" }}>Sign in to your Binos dashboard.</p>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <button onClick={handleGoogle} disabled={loading}
          style={{ width: "100%", padding: "12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontFamily: "inherit" }}>
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.2)", textTransform: "uppercase", letterSpacing: "0.1em" }}>or</span>
          <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.08)" }} />
        </div>

        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} placeholder="you@fund.com" style={inputStyle}
            onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
        </div>

        <div>
          <label style={labelStyle}>Password</label>
          <div style={{ position: "relative" }}>
            <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()} placeholder="••••••••" autoComplete="current-password" style={{ ...inputStyle, paddingRight: 44 }}
              onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            <button onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", padding: 0 }}>
              {showPassword ? <EyeOff style={{ width: 15, height: 15 }} /> : <Eye style={{ width: 15, height: 15 }} />}
            </button>
          </div>
        </div>

        <button onClick={onForgot} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.3)", fontSize: 12, fontWeight: 600, cursor: "pointer", textAlign: "right", fontFamily: "inherit", padding: 0, marginTop: -8 }}>Forgot password?</button>

        {error && <div style={{ padding: "10px 14px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 9 }}><p style={{ fontSize: 12, fontWeight: 600, color: "#dc2626", margin: 0 }}>{error}</p></div>}

        <button onClick={handleLogin} disabled={loading}
          style={{ width: "100%", padding: "13px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, color: "#fff", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
          {loading ? <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> : null}
          {loading ? "Signing in..." : "Sign in"} {!loading && <ArrowUpRight style={{ width: 15, height: 15 }} />}
        </button>
      </div>

      <div style={{ marginTop: 24, textAlign: "center" }}>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", margin: 0 }}>
          Don't have an account?{" "}
          <button onClick={onSwitchToSignup} style={{ background: "none", border: "none", color: "#dc2626", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Apply for access</button>
        </p>
      </div>
    </div>
  );
}

// ── MULTI-STEP SIGNUP ──
function SignupForm({ onSwitchToLogin }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Step 1
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  // Step 2
  const [fundName, setFundName] = useState("");
  const [role, setRole] = useState("");
  const [aum, setAum] = useState("");
  const [stages, setStages] = useState([]);
  const [sectors, setSectors] = useState([]);

  // Step 3
  const [website, setWebsite] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [hearAbout, setHearAbout] = useState("");

  const toggleStage = (s) => setStages(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const toggleSector = (s) => setSectors(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const validateStep1 = () => {
    if (!fullName.trim()) { setError("Please enter your full name."); return false; }
    if (!email.trim()) { setError("Please enter your email."); return false; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return false; }
    if (password !== confirmPassword) { setError("Passwords don't match."); return false; }
    return true;
  };

  const validateStep2 = () => {
    if (!fundName.trim()) { setError("Please enter your fund name."); return false; }
    if (!role) { setError("Please select your role."); return false; }
    if (!aum) { setError("Please select your AUM range."); return false; }
    if (stages.length === 0) { setError("Please select at least one stage."); return false; }
    if (sectors.length === 0) { setError("Please select at least one sector."); return false; }
    return true;
  };

  const handleNext = () => {
    setError("");
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleSubmit = async () => {
    setLoading(true); setError("");
    try {
      const { data, error: signupError } = await supabase.auth.signUp({ email, password });
      if (signupError) { setError(friendlyError(signupError.message)); setLoading(false); return; }

      if (data.user) {
        await supabase.from("profiles").upsert({
          id: data.user.id,
          full_name: sanitizeText(fullName),
          fund_name: sanitizeText(fundName),
          role,
          aum,
          stages: stages,
          sectors: sectors,
          website: website ? sanitizeText(website) : null,
          linkedin: linkedin ? sanitizeText(linkedin) : null,
          hear_about: hearAbout || null,
          status: "pending",
          onboarded: false,
        });
      }

      setSubmitted(true);
    } catch (e) {
      setError("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const progress = (step / 3) * 100;

  if (submitted) {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 64, height: 64, background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
          <CheckCircle2 style={{ width: 32, height: 32, color: "#16a34a" }} />
        </div>
        <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 26, fontWeight: 400, color: "#fff", margin: "0 0 10px", letterSpacing: "-0.5px" }}>Application received.</h2>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", lineHeight: 1.7, margin: "0 0 24px" }}>
          We review every application personally. You'll hear from us within <strong style={{ color: "#fff" }}>24 hours</strong>.
        </p>
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "16px 20px", marginBottom: 24, textAlign: "left" }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 10px" }}>What happens next</p>
          {["We review your fund profile", "You get an email — approved or not", "Approved funds get instant dashboard access"].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 20, height: 20, background: "rgba(220,38,38,0.1)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: "#dc2626", flexShrink: 0 }}>{i + 1}</div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", margin: 0 }}>{item}</p>
            </div>
          ))}
        </div>
        <button onClick={onSwitchToLogin}
          style={{ width: "100%", padding: "12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.6)", cursor: "pointer", fontFamily: "inherit" }}>
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div autoComplete="off">
      {/* Progress bar */}
      <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 100, marginBottom: 24, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: "#dc2626", borderRadius: 100, transition: "width 0.4s ease" }} />
      </div>

      {/* Step indicator */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20 }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{ width: i === step ? 20 : 6, height: 6, borderRadius: 100, background: i === step ? "#dc2626" : i < step ? "rgba(220,38,38,0.4)" : "rgba(255,255,255,0.1)", transition: "all 0.3s" }} />
        ))}
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", fontWeight: 600, marginLeft: 6 }}>Step {step} of 3</span>
      </div>

      {/* STEP 1 — Account */}
      {step === 1 && (
        <div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 26, fontWeight: 400, color: "#fff", margin: "0 0 6px", letterSpacing: "-0.5px" }}>Create your account.</h1>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", margin: "0 0 24px" }}>Start your application for Binos early access.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelStyle}>Full Name</label>
              <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Anas Khakhu" style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            </div>
            <div>
              <label style={labelStyle}>Work Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@fund.com" style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters" autoComplete="new-password" style={{ ...inputStyle, paddingRight: 44 }}
                  onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
                <button onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.3)", padding: 0 }}>
                  {showPassword ? <EyeOff style={{ width: 15, height: 15 }} /> : <Eye style={{ width: 15, height: 15 }} />}
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            </div>
          </div>
        </div>
      )}

      {/* STEP 2 — Fund Profile */}
      {step === 2 && (
        <div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 26, fontWeight: 400, color: "#fff", margin: "0 0 6px", letterSpacing: "-0.5px" }}>Your fund profile.</h1>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", margin: "0 0 24px" }}>Help us personalise Binos for your fund.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={labelStyle}>Fund Name</label>
              <input type="text" value={fundName} onChange={e => setFundName(e.target.value)} placeholder="e.g. Sequoia, Blume Ventures" style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            </div>
            <div>
              <label style={labelStyle}>Your Role</label>
              <PillSelect options={ROLES} selected={role} onToggle={setRole} multi={false} />
            </div>
            <div>
              <label style={labelStyle}>Fund AUM</label>
              <PillSelect options={AUM_OPTIONS} selected={aum} onToggle={setAum} multi={false} />
            </div>
            <div>
              <label style={labelStyle}>Stage Focus <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(select all that apply)</span></label>
              <PillSelect options={STAGES} selected={stages} onToggle={toggleStage} />
            </div>
            <div>
              <label style={labelStyle}>Sector Focus <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(select all that apply)</span></label>
              <PillSelect options={SECTORS} selected={sectors} onToggle={toggleSector} />
            </div>
          </div>
        </div>
      )}

      {/* STEP 3 — Optional */}
      {step === 3 && (
        <div>
          <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 26, fontWeight: 400, color: "#fff", margin: "0 0 6px", letterSpacing: "-0.5px" }}>Almost done.</h1>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", margin: "0 0 24px" }}>Optional details — skip if you'd like.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelStyle}>Fund Website <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
              <input type="text" value={website} onChange={e => setWebsite(e.target.value)} placeholder="fund.com" style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            </div>
            <div>
              <label style={labelStyle}>Your LinkedIn <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></label>
              <input type="text" value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="linkedin.com/in/yourname" style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            </div>
            <div>
              <label style={labelStyle}>How did you hear about Binos?</label>
              <PillSelect options={HEAR_OPTIONS} selected={hearAbout} onToggle={setHearAbout} multi={false} />
            </div>

            {/* Summary */}
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 16px" }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 10px" }}>Application Summary</p>
              {[
                { label: "Name", value: fullName },
                { label: "Fund", value: fundName },
                { label: "Role", value: role },
                { label: "AUM", value: aum },
                { label: "Stages", value: stages.join(", ") },
                { label: "Sectors", value: sectors.slice(0, 3).join(", ") + (sectors.length > 3 ? ` +${sectors.length - 3}` : "") },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontWeight: 600 }}>{item.label}</span>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontWeight: 600, textAlign: "right", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.value || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: "10px 14px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 9, marginTop: 14 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: "#dc2626", margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
        {step > 1 && (
          <button onClick={() => { setStep(step - 1); setError(""); }}
            style={{ padding: "13px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.5)", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
            <ArrowLeft style={{ width: 14, height: 14 }} />
          </button>
        )}
        <button onClick={step < 3 ? handleNext : handleSubmit} disabled={loading}
          style={{ flex: 1, padding: "13px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, color: "#fff", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
          {loading ? <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> : null}
          {loading ? "Submitting..." : step < 3 ? "Continue →" : "Submit Application ↗"}
        </button>
      </div>

      <div style={{ marginTop: 20, textAlign: "center" }}>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", margin: 0 }}>
          Already have an account?{" "}
          <button onClick={onSwitchToLogin} style={{ background: "none", border: "none", color: "#dc2626", fontWeight: 700, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }}>Sign in</button>
        </p>
      </div>
    </div>
  );
}

// ── FORGOT PASSWORD ──
function ForgotForm({ onBack }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleReset = async () => {
    if (!email) { setError("Please enter your email."); return; }
    setLoading(true); setError("");
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/dashboard` });
    if (error) { setError(friendlyError(error.message)); setLoading(false); return; }
    setSuccess("Password reset link sent. Check your inbox.");
    setLoading(false);
  };

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, padding: 0, marginBottom: 20 }}>
        <ArrowLeft style={{ width: 13, height: 13 }} /> Back to sign in
      </button>
      <h1 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 26, fontWeight: 400, color: "#fff", margin: "0 0 6px", letterSpacing: "-0.5px" }}>Reset your password.</h1>
      <p style={{ fontSize: 13, color: "rgba(255,255,255,0.35)", margin: "0 0 24px" }}>We'll send a reset link to your email.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleReset()} placeholder="you@fund.com" style={inputStyle}
            onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
        </div>
        {error && <div style={{ padding: "10px 14px", background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 9 }}><p style={{ fontSize: 12, fontWeight: 600, color: "#dc2626", margin: 0 }}>{error}</p></div>}
        {success && <div style={{ padding: "10px 14px", background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 9 }}><p style={{ fontSize: 12, fontWeight: 600, color: "#16a34a", margin: 0 }}>{success}</p></div>}
        <button onClick={handleReset} disabled={loading}
          style={{ width: "100%", padding: "13px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800, color: "#fff", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
          {loading ? <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" /> : null}
          {loading ? "Sending..." : "Send reset link"}
        </button>
      </div>
    </div>
  );
}

// ── MAIN ──
export default function AuthPage({ onLogin }) {
  const [mode, setMode] = useState("login");

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', system-ui, sans-serif", position: "relative", overflow: "hidden", padding: "40px 24px" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;900&family=DM+Serif+Display:ital@0;1&display=swap" rel="stylesheet" />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)", backgroundSize: "48px 48px", pointerEvents: "none" }} />
      <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 600, height: 400, background: "radial-gradient(ellipse, rgba(220,38,38,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{ width: "100%", maxWidth: 460, position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32, justifyContent: "center" }}>
          <img src={logo} style={{ width: 36, height: 36 }} alt="Binos" />
          <span style={{ fontWeight: 900, fontSize: 22, color: "#fff", letterSpacing: "-0.5px" }}>Binos</span>
        </div>

        <div style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "36px 32px" }}>
          {mode === "login" && <LoginForm onLogin={onLogin} onSwitchToSignup={() => setMode("signup")} onForgot={() => setMode("forgot")} />}
          {mode === "signup" && <SignupForm onSwitchToLogin={() => setMode("login")} />}
          {mode === "forgot" && <ForgotForm onBack={() => setMode("login")} />}
        </div>

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <a href="/" style={{ fontSize: 12, color: "rgba(255,255,255,0.2)", textDecoration: "none", fontWeight: 500 }}
            onMouseEnter={e => e.target.style.color = "rgba(255,255,255,0.5)"}
            onMouseLeave={e => e.target.style.color = "rgba(255,255,255,0.2)"}>
            ← Back to home
          </a>
        </div>
      </div>
    </div>
  );
}
